/**
 * useEventFormState Hook
 *
 * State management for the CalendarEventModal form.
 * Extracted for single responsibility and testability.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Alert } from 'react-native';
import type {
  CalendarEvent,
  CreateCalendarEventInput,
  UpdateCalendarEventInput,
  RecurrenceRule,
  CalendarTransparency,
  EventReminder,
  EventParticipant,
} from '@/types/events';
import type { TransportAssignment } from '@/types/transport';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import { EventDescriptionSectionParser } from '@/lib/utils/EventDescriptionSectionParser';
import { EventDescriptionParser } from '@/lib/utils/EventDescriptionParser';
import { EventParticipantsManager } from '@/lib/managers/EventParticipantsManager';
import { EventFormInitializer } from '@/lib/managers/EventFormInitializer';
import { PillInviteManager } from '@/lib/managers/PillInviteManager';
import { formatDate, formatTime, addMinutesToDate, parseDateTime, compareDateTimes } from '@/lib/utils/DateTimeFormatUtils';

/** Validate YYYY-MM-DD format */
const isValidDateFormat = (s: string | null | undefined): s is string =>
  s != null && /^\d{4}-\d{2}-\d{2}$/.test(s);

/** Validate HH:mm 24h format */
const isValidTimeFormat = (s: string | null | undefined): s is string =>
  s != null && /^\d{2}:\d{2}$/.test(s);

export type EventFormMode = 'create' | 'edit';

export type InviteeSelectable = {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  isSelf: boolean;
  selected: boolean;
};

type UseEventFormStateOptions = {
  mode: EventFormMode;
  initialEvent?: CalendarEvent | null;
  preselectedDate?: string | null;
  preselectedTime?: string | null;
  defaultTitle?: string;
  defaultDescription?: string;
  writableCalendars: DeviceCalendar[];
  defaultCalendarId?: string | null;
  familyMembers: FamilyMember[];
  calendarMappings?: CalendarMemberMapping[];
  session: { user: { id: string; email?: string; user_metadata?: { avatar_url?: string; full_name?: string } } } | null;
};

export function useEventFormState(options: UseEventFormStateOptions) {
  const { mode, initialEvent, preselectedDate, preselectedTime, defaultTitle, defaultDescription, writableCalendars, defaultCalendarId, familyMembers, calendarMappings = [], session } = options;

  // Core form state
  const [title, setTitle] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [startDate, setStartDate] = useState(() => formatDate(new Date()));
  const [startTime, setStartTime] = useState<string | null>(() => formatTime(new Date()));
  const [endDate, setEndDate] = useState(() => formatDate(new Date()));
  const [endTime, setEndTime] = useState<string | null>(() => formatTime(addMinutesToDate(new Date(), 60)));
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [recurrence, setRecurrence] = useState<RecurrenceRule | null>(null);
  const [transparency, setTransparency] = useState<CalendarTransparency>('opaque');
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [reminders, setReminders] = useState<EventReminder[]>([]);
  const [organizer, setOrganizer] = useState<{ email?: string; name?: string | null } | null>(null);
  const [transport, setTransport] = useState<TransportAssignment[]>([]);
  const [taskColor, setTaskColor] = useState<string | null>(null);
  const [meToggleOff, setMeToggleOff] = useState(false);

  // Derived: is the currently selected calendar linked to the "Me" member?
  const isCalendarLinkedToMe = useMemo(
    () => EventFormInitializer.isCalendarLinkedToMe(selectedCalendarId, familyMembers, calendarMappings),
    [selectedCalendarId, familyMembers, calendarMappings],
  );

  // Derived: Me is active when calendar is linked AND user hasn't toggled it off
  const meIsActive = isCalendarLinkedToMe && !meToggleOff;

  // Auto-adjust end date/time when start changes and exceeds end
  const handleSetStartDate = useCallback((newStartDate: string) => {
    // Check if new start datetime exceeds current end datetime
    if (compareDateTimes(newStartDate, startTime, endDate, endTime, allDay) > 0) {
      // New start is after end - adjust end to 1 hour after start
      const startDateTime = parseDateTime(newStartDate, startTime);
      const newEndDateTime = addMinutesToDate(startDateTime, 60);
      setEndDate(formatDate(newEndDateTime));
      if (!allDay) {
        setEndTime(formatTime(newEndDateTime));
      }
    }
    setStartDate(newStartDate);
  }, [startTime, endDate, endTime, allDay]);

  const handleSetStartTime = useCallback((newStartTime: string | null) => {
    if (!allDay && newStartTime) {
      // Check if new start datetime exceeds current end datetime
      if (compareDateTimes(startDate, newStartTime, endDate, endTime, allDay) > 0) {
        // New start is after end - adjust end to 1 hour after start
        const startDateTime = parseDateTime(startDate, newStartTime);
        const newEndDateTime = addMinutesToDate(startDateTime, 60);
        setEndDate(formatDate(newEndDateTime));
        setEndTime(formatTime(newEndDateTime));
      }
    }
    setStartTime(newStartTime);
  }, [startDate, endDate, endTime, allDay]);

  // Change tracking state
  const [isInitialized, setIsInitialized] = useState(false);
  const initialSnapshotRef = useRef<{
    title: string;
    allDay: boolean;
    startDate: string;
    startTime: string | null;
    endDate: string;
    endTime: string | null;
    selectedCalendarId: string | null;
    location: string;
    description: string;
    recurrence: RecurrenceRule | null;
    reminders: EventReminder[];
    participantIds: string[];
    transport: TransportAssignment[];
    taskColor: string | null;
    meToggleOff: boolean;
  } | null>(null);

  // Initialize form when mode/initialEvent changes
  const initializeForm = useCallback(() => {
    // Reset initialization state
    setIsInitialized(false);
    initialSnapshotRef.current = null;

    if (mode === 'create') {
      const now = new Date();
      const initialTitle = defaultTitle ?? '';
      const validDate = isValidDateFormat(preselectedDate) ? preselectedDate : null;
      const validTime = isValidTimeFormat(preselectedTime) ? preselectedTime : null;
      const initialStartDate = validDate ?? formatDate(now);
      const initialStartTime = validTime ?? formatTime(now);
      const startDateTime = validTime
        ? parseDateTime(initialStartDate, validTime)
        : now;
      const initialEndDate = validDate ?? formatDate(now);
      const initialEndTime = formatTime(addMinutesToDate(startDateTime, 60));
      const initialCalendarId = defaultCalendarId ?? writableCalendars[0]?.id ?? null;
      const initialDescription = defaultDescription ?? '';

      setTitle(initialTitle);
      setAllDay(false);
      setStartDate(initialStartDate);
      setStartTime(initialStartTime);
      setEndDate(initialEndDate);
      setEndTime(initialEndTime);
      setSelectedCalendarId(initialCalendarId);
      setLocation('');
      setDescription(initialDescription);
      setRecurrence(null);
      setTransparency('opaque');
      setReminders([]);
      setTransport([]);
      setTaskColor(null);
      setMeToggleOff(false);

      // Initialize organizer from selected calendar's owner account
      // This ensures the organizer matches the "on calendar" setting
      const initialCalendar = writableCalendars.find(c => c.id === initialCalendarId);
      const ownerEmail = initialCalendar?.ownerAccount ?? session?.user?.email;
      const ownerName = ownerEmail === session?.user?.email
        ? (session?.user?.user_metadata?.full_name ?? ownerEmail ?? null)
        : (initialCalendar?.title ?? ownerEmail ?? null);

      const initialOrganizer = ownerEmail
        ? { email: ownerEmail, name: ownerName }
        : null;
      setOrganizer(initialOrganizer);

      // Initialize participants with organiser + "Me" label (if calendar is linked to Me)
      const initialParticipants = EventFormInitializer.initializeCreateModeParticipants({
        ownerEmail,
        ownerName,
        userId: ownerEmail === session?.user?.email && session?.user?.id ? session.user.id : undefined,
        avatarUrl: ownerEmail === session?.user?.email ? session?.user?.user_metadata?.avatar_url : undefined,
        calendarId: initialCalendarId,
        familyMembers,
        calendarMappings,
      });
      setParticipants(initialParticipants);

      // Capture initial snapshot for change tracking
      initialSnapshotRef.current = {
        title: initialTitle,
        allDay: false,
        startDate: initialStartDate,
        startTime: initialStartTime,
        endDate: initialEndDate,
        endTime: initialEndTime,
        selectedCalendarId: initialCalendarId,
        location: '',
        description: initialDescription,
        recurrence: null,
        reminders: [],
        participantIds: initialParticipants.map(p => p.id),
        transport: [],
        taskColor: null,
        meToggleOff: false,
      };
      setIsInitialized(true);
    } else if (mode === 'edit' && initialEvent) {
      const initialAllDay = initialEvent.allDay ?? (!initialEvent.startTime && !initialEvent.endTime);

      setTitle(initialEvent.title);
      setAllDay(initialAllDay);
      setStartDate(initialEvent.startDate);
      setStartTime(initialEvent.startTime ?? null);
      setEndDate(initialEvent.endDate ?? initialEvent.startDate);
      setEndTime(initialEvent.endTime ?? null);
      setSelectedCalendarId(initialEvent.deviceCalendarId ?? null);
      setLocation(initialEvent.location ?? '');

      // Initialize organizer from event
      setOrganizer(initialEvent.organizer ?? null);

      // Parse description sections (tasks, family members, plain text)
      console.log('[initializeForm] initialEvent.description:', initialEvent.description);
      const parsedDescription = EventDescriptionSectionParser.parse(initialEvent.description);
      console.log('[initializeForm] parsed familyMemberIds:', parsedDescription.familyMemberIds);

      // Re-serialize plain text + tasks without section markers for ChecklistDescriptionField
      const textLines = parsedDescription.plainText.trim()
        ? parsedDescription.plainText.split('\n')
        : [];
      const descriptionForField = EventDescriptionParser.serialize(
        textLines,
        parsedDescription.tasks
      );
      setDescription(descriptionForField);
      setTaskColor(parsedDescription.taskColor);
      setMeToggleOff(parsedDescription.meToggleOff);

      const initialReminders = initialEvent.extendedProperties?.reminders ?? [];

      // Initialize participants from event (with backward compatibility)
      console.log('[initializeForm] initialEvent.extendedProperties:', initialEvent.extendedProperties);
      let initialParticipants: EventParticipant[];
      if (initialEvent.extendedProperties?.participants) {
        // New format: use participants directly
        console.log('[initializeForm] Using participants from extendedProperties');
        initialParticipants = initialEvent.extendedProperties.participants;
      } else {
        // Legacy format: migrate from invitees + family member IDs
        console.log('[initializeForm] Using legacy migration path');
        const legacyInvitees = initialEvent.extendedProperties?.invitees ?? [];
        initialParticipants = EventParticipantsManager.buildParticipantsFromLegacy(
          legacyInvitees,
          parsedDescription.familyMemberIds,
          familyMembers,
          session?.user?.id,
        );
      }
      // Sync labels from existing invites for calendar-linked members
      // Strip any legacy Me labels — Me is now derived from calendar linkage + metoggle
      const meMember = familyMembers.find(fm => fm.isMe);
      initialParticipants = EventFormInitializer.enrichEditModeParticipants({
        participants: initialParticipants,
        familyMembers,
        calendarMappings,
        calendars: writableCalendars,
        meMemberId: meMember?.id,
      });
      console.log('[initializeForm] initialParticipants:', initialParticipants);
      setParticipants(initialParticipants);

      // Initialize transport from parsed description
      const initialTransport = parsedDescription.transport ?? [];
      setTransport(initialTransport);

      setRecurrence(initialEvent.recurrence ?? null);
      setTransparency(initialEvent.transparency ?? 'opaque');
      setReminders(initialReminders);

      // Capture initial snapshot for change tracking
      initialSnapshotRef.current = {
        title: initialEvent.title,
        allDay: initialAllDay,
        startDate: initialEvent.startDate,
        startTime: initialEvent.startTime ?? null,
        endDate: initialEvent.endDate ?? initialEvent.startDate,
        endTime: initialEvent.endTime ?? null,
        selectedCalendarId: initialEvent.deviceCalendarId ?? null,
        location: initialEvent.location ?? '',
        description: descriptionForField,
        recurrence: initialEvent.recurrence ?? null,
        reminders: initialReminders,
        participantIds: initialParticipants.map(p => p.id),
        transport: initialTransport,
        taskColor: parsedDescription.taskColor,
        meToggleOff: parsedDescription.meToggleOff,
      };
      setIsInitialized(true);
    }
  }, [mode, initialEvent, preselectedDate, preselectedTime, defaultTitle, defaultDescription, defaultCalendarId, writableCalendars, familyMembers, calendarMappings, session]);

  // Sync attendees when initialEvent is enriched asynchronously (e.g. AttendeeResolver)
  // This handles the case where the modal opens immediately and attendees arrive later.
  // Also updates the initial snapshot so change-tracking doesn't flag this as a user edit.
  useEffect(() => {
    if (!isInitialized || mode !== 'edit' || !initialEvent?.extendedProperties?.participants) {
      return;
    }

    const incomingAttendees = initialEvent.extendedProperties.participants
      .filter(p => p.role === 'invited' || p.role === 'organiser');

    if (incomingAttendees.length === 0) {
      return;
    }

    setParticipants(current => {
      const existingLabels = current.filter(p => p.role === 'label');
      const existingAttendeeEmails = new Set(
        current.filter(p => p.role === 'invited' || p.role === 'organiser')
          .map(p => p.email?.toLowerCase()),
      );

      const newAttendees = incomingAttendees.filter(
        a => a.email && !existingAttendeeEmails.has(a.email.toLowerCase()),
      );

      if (newAttendees.length === 0 && existingAttendeeEmails.size > 0) {
        return current;
      }

      const merged = [...incomingAttendees, ...existingLabels];

      // Update snapshot so this async enrichment isn't treated as a user edit
      if (initialSnapshotRef.current) {
        initialSnapshotRef.current.participantIds = merged.map(p => p.id);
      }

      return merged;
    });
  }, [isInitialized, mode, initialEvent?.extendedProperties?.participants]);

  // CRITICAL: All-day toggle handler with proper time initialization
  const handleToggleAllDay = useCallback((value: boolean) => {
    setAllDay(value);

    if (value) {
      // Toggling ON - clear times
      setStartTime(null);
      setEndTime(null);
    } else {
      // Toggling OFF - set default times SYNCHRONOUSLY
      const now = new Date();
      const newStartTime = formatTime(now);
      const newEndTime = formatTime(addMinutesToDate(now, 60));
      setStartTime(newStartTime);
      setEndTime(newEndTime);
    }
  }, []);

  // Participant handlers
  const handleAddEmailInvitee = useCallback((email: string) => {
    setParticipants(current => {
      const result = EventParticipantsManager.addEmailInvitee(
        current,
        email,
        session?.user?.id
      );

      if (result.error) {
        console.warn('[handleAddEmailInvitee] Failed to add invitee:', result.error);
        // TODO: Could add state to show error to user in future enhancement
      } else {
        console.log('[handleAddEmailInvitee] Added email invitee:', email);
      }

      return result.participants;
    });
  }, [session]);

  const handleRemoveParticipant = useCallback((id: string) => {
    setParticipants(current =>
      EventParticipantsManager.removeParticipant(current, id)
    );
  }, []);

  const handleToggleFamilyLabel = useCallback((familyMemberId: string, familyMember: FamilyMember) => {
    setParticipants(current =>
      EventParticipantsManager.toggleFamilyLabel(current, familyMemberId, familyMember)
    );
  }, []);

  const handleToggleForMember = useCallback((familyMember: FamilyMember) => {
    setParticipants(current => {
      const isCurrentlySelected = current.some(
        p => p.role === 'label' && p.familyMemberId === familyMember.id,
      );

      const result = EventParticipantsManager.toggleForMember(
        current,
        familyMember,
        meIsActive,
      );

      if (result.blocked) {
        Alert.alert(
          'Cannot remove',
          'At least one person must be selected.',
        );
        return current;
      }

      // On deselect, also remove any pill-linked invitees for this member
      if (isCurrentlySelected) {
        return PillInviteManager.removeLinkedInvitees(result.participants, familyMember.id);
      }

      return result.participants;
    });
  }, [meIsActive]);

  const handleAddPillLinkedInvite = useCallback((familyMember: FamilyMember, email: string) => {
    setParticipants(current =>
      PillInviteManager.addLinkedInvitee(current, email, familyMember),
    );
  }, []);

  // Me toggle handler — toggles meToggleOff, respecting min-1 rule
  const handleToggleMeForEvent = useCallback(() => {
    if (!isCalendarLinkedToMe) return;

    if (!meToggleOff) {
      // Trying to toggle Me OFF — check min-1 rule
      const labelCount = EventParticipantsManager.countForLabels(participants);
      if (labelCount === 0) {
        Alert.alert('Cannot remove', 'At least one person must be selected.');
        return;
      }
    }

    setMeToggleOff(prev => !prev);
  }, [isCalendarLinkedToMe, meToggleOff, participants]);

  // Reminder handlers
  const handleAddReminder = useCallback(() => {
    // Calculate 30 minutes before event start
    const [year, month, day] = startDate.split('-').map(Number);
    const eventDateTime = new Date(year, month - 1, day);
    if (startTime) {
      const [hours, minutes] = startTime.split(':').map(Number);
      eventDateTime.setHours(hours, minutes);
    } else {
      // Default to 9 AM for all-day events
      eventDateTime.setHours(9, 0);
    }
    const reminderDateTime = new Date(eventDateTime.getTime() - 30 * 60 * 1000);

    const newReminder: EventReminder = {
      id: `reminder-${Date.now()}`,
      date: formatDate(reminderDateTime),
      time: formatTime(reminderDateTime),
    };
    setReminders(current => [...current, newReminder]);
  }, [startDate, startTime]);

  const handleUpdateReminder = useCallback((id: string, updates: Partial<EventReminder>) => {
    setReminders(current =>
      current.map(reminder =>
        reminder.id === id ? { ...reminder, ...updates } : reminder,
      ),
    );
  }, []);

  const handleRemoveReminder = useCallback((id: string) => {
    setReminders(current => current.filter(r => r.id !== id));
  }, []);

  // Handle calendar change - updates organizer to match calendar owner
  const handleCalendarChange = useCallback((calendarId: string | null) => {
    setSelectedCalendarId(calendarId);
    setMeToggleOff(false); // Reset Me toggle when calendar changes

    // Only update organizer in create mode - edit mode keeps original organizer
    if (mode !== 'create' || !calendarId) return;

    // Find the selected calendar to get its owner account
    const selectedCalendar = writableCalendars.find(c => c.id === calendarId);
    if (!selectedCalendar) return;

    // Use calendar owner account if available (Android), otherwise fall back to session user
    const ownerEmail = selectedCalendar.ownerAccount ?? session?.user?.email;
    if (!ownerEmail) return;

    // Determine the organizer name - use session user name if email matches, otherwise use calendar title
    const organizerName = ownerEmail === session?.user?.email
      ? (session?.user?.user_metadata?.full_name ?? ownerEmail)
      : selectedCalendar.title;

    // Update organizer state
    setOrganizer({ email: ownerEmail, name: organizerName });

    // Update organizer in participants array
    setParticipants(current => {
      // Remove existing organiser
      const withoutOrganiser = current.filter(p => p.role !== 'organiser');
      // Add new organiser based on calendar owner
      return EventParticipantsManager.addOrganiser(
        withoutOrganiser,
        session?.user?.id ?? `calendar-${calendarId}`,
        organizerName,
        ownerEmail,
        ownerEmail === session?.user?.email ? session?.user?.user_metadata?.avatar_url : undefined,
      );
    });
  }, [mode, writableCalendars, session]);

  // Build save input
  const buildSaveInput = useCallback((): (CreateCalendarEventInput | UpdateCalendarEventInput) & { targetCalendarId?: string } => {
    // Validate times when not all-day
    if (!allDay && (!startTime || !endTime)) {
      throw new Error('Missing time values for non-all-day event');
    }

    // Parse description (from ChecklistDescriptionField) to separate text from tasks
    const parsedDescription = EventDescriptionParser.parse(description);
    const plainText = parsedDescription.textLines.join('\n');

    // Extract family member IDs from participants
    const familyMemberIds = EventParticipantsManager.getFamilyMemberIds(participants);

    // Serialize with section markers for device calendar persistence
    console.log('[buildSaveInput] familyMemberIds:', familyMemberIds);
    console.log('[buildSaveInput] participants:', participants);
    console.log('[buildSaveInput] transport:', transport);

    const serializedDescription = EventDescriptionSectionParser.serialize(
      plainText.trim(),
      parsedDescription.checklistItems,
      familyMemberIds,
      transport,
      taskColor,
      meToggleOff,
    );
    console.log('[buildSaveInput] serializedDescription:', serializedDescription);

    const baseInput = {
      title: title.trim(),
      allDay,
      startDate,
      startTime: allDay ? null : startTime,
      endDate,
      endTime: allDay ? null : endTime,
      description: serializedDescription || null,
      location: location.trim() || null,
      transparency,
      recurrence,
      extendedProperties: {
        participants,
        reminders,
      },
    };

    if (mode === 'create') {
      return {
        ...baseInput,
        targetCalendarId: selectedCalendarId ?? undefined,
      };
    } else {
      // Only include recurrence if user changed it — avoids overwriting
      // the device calendar's original (potentially richer) recurrence data
      const snap = initialSnapshotRef.current;
      const recurrenceChanged = JSON.stringify(recurrence) !== JSON.stringify(snap?.recurrence);

      return {
        ...baseInput,
        recurrence: recurrenceChanged ? recurrence : undefined,
        id: initialEvent!.id,
      };
    }
  }, [
    mode, title, allDay, startDate, startTime, endDate, endTime,
    description, location, transparency, recurrence, participants,
    reminders, transport, taskColor, meToggleOff, selectedCalendarId, initialEvent,
  ]);

  // Change tracking - detect if form has been modified from initial state
  const hasChanges = useMemo(() => {
    const snap = initialSnapshotRef.current;
    if (!snap || !isInitialized) return false;

    // Get current participant IDs
    const currentParticipantIds = participants.map(p => p.id).sort();
    const snapParticipantIds = [...snap.participantIds].sort();

    return (
      title !== snap.title ||
      allDay !== snap.allDay ||
      startDate !== snap.startDate ||
      startTime !== snap.startTime ||
      endDate !== snap.endDate ||
      endTime !== snap.endTime ||
      selectedCalendarId !== snap.selectedCalendarId ||
      location !== snap.location ||
      description !== snap.description ||
      JSON.stringify(recurrence) !== JSON.stringify(snap.recurrence) ||
      JSON.stringify(reminders) !== JSON.stringify(snap.reminders) ||
      JSON.stringify(currentParticipantIds) !== JSON.stringify(snapParticipantIds) ||
      JSON.stringify(transport) !== JSON.stringify(snap.transport) ||
      taskColor !== snap.taskColor ||
      meToggleOff !== snap.meToggleOff
    );
  }, [
    isInitialized, title, allDay, startDate, startTime, endDate, endTime,
    selectedCalendarId, location, description, recurrence, reminders,
    participants, transport, taskColor, meToggleOff,
  ]);

  // Update description snapshot after instant saves (prevents false "unsaved changes" warnings)
  const updateDescriptionSnapshot = useCallback((newDescription: string) => {
    if (initialSnapshotRef.current) {
      initialSnapshotRef.current.description = newDescription;
    }
  }, []);

  // Validation
  const isValid = useMemo(() => {
    if (!title.trim()) return false;
    if (!selectedCalendarId) return false;
    if (!allDay && (!startTime || !endTime)) return false;
    return true;
  }, [title, selectedCalendarId, allDay, startTime, endTime]);

  return {
    // State values
    state: {
      title,
      allDay,
      startDate,
      startTime,
      endDate,
      endTime,
      selectedCalendarId,
      location,
      description,
      recurrence,
      transparency,
      participants,
      reminders,
      organizer,
      transport,
      taskColor,
      meToggleOff,
      meIsActive,
      isCalendarLinkedToMe,
    },
    // Setters
    setters: {
      setTitle,
      setAllDay,
      setStartDate: handleSetStartDate,
      setStartTime: handleSetStartTime,
      setEndDate,
      setEndTime,
      setSelectedCalendarId,
      setLocation,
      setDescription,
      setRecurrence,
      setTransparency,
      setParticipants,
      setReminders,
      setOrganizer,
      setTransport,
      setTaskColor,
    },
    // Handlers
    handlers: {
      initializeForm,
      handleToggleAllDay,
      handleAddEmailInvitee,
      handleRemoveParticipant,
      handleToggleFamilyLabel,
      handleToggleForMember,
      handleAddPillLinkedInvite,
      handleToggleMeForEvent,
      handleAddReminder,
      handleUpdateReminder,
      handleRemoveReminder,
      handleCalendarChange,
      buildSaveInput,
      updateDescriptionSnapshot,
    },
    // Validation & Change tracking
    isValid,
    hasChanges,
  };
}
